import {
  AccessTime,
  ChevronLeft,
  ChevronRight,
  KeyboardArrowDown,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import apiService from "../../../service/Api";
import { tokens } from "../../../theme";
import JobCardItemDetailsModal from "./JobCardItemDetailsModal";
import JobCardActivityMinimisedPill from "./JobCardActivityMinimisedPill";

// ─── Types ────────────────────────────────────────────────────────────────────

type JobCardBucket = "overdue" | "in-progress" | "needs-review";
type Kind = "assignment" | "proposal";

interface RawAssignment {
  id: number;
  source_proposal_id?: number | null;
  intern_name: string;
  intern_profile_picture?: string | null;
  title: string | null;
  description: string | null;
  due_date: string | null;
  reward: number | string | null;
  status: string;
  created_at: string;
}

interface RawProposal {
  id: number;
  intern_name: string;
  intern_profile_picture?: string | null;
  title: string | null;
  description: string | null;
  deadline: string | null;
  estimated_reward: number | string | null;
  status: string;
  created_at: string;
}

export interface JobCardItem {
  id: string; // `${kind}-${id}` so assignments/proposals never collide
  rawId: number; // numeric backend pk — required for approve/reject calls
  kind: Kind;
  sourceProposalId?: number | null;
  title: string;
  internName: string;
  internProfilePicture: string | null;
  description: string;
  dueDate: string | null;
  reward: number;
  status: string;
  createdAt: string;
  bucket: JobCardBucket;
}

type MobileTab = "summary" | JobCardBucket;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const isOverdue = (dueDate: string | null): boolean => {
  if (!dueDate) return false;
  const due = new Date(dueDate);
  due.setHours(23, 59, 59, 999);
  return due < new Date();
};

const resolveBucket = (
  kind: Kind,
  status: string,
  dueDate: string | null,
): JobCardBucket | null => {
  // Resolved items (approved/rejected) don't belong in an "upcoming" widget.
  if (status === "approved" || status === "rejected") return null;

  const needsReview =
    kind === "assignment" ? status === "completed" : status === "pending";
  if (needsReview) return "needs-review";

  if (isOverdue(dueDate)) return "overdue";

  return "in-progress"; // sent/accepted/needs_revision
};

const formatDate = (dateStr: string | null): string => {
  if (!dateStr) return "No date";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const formatTimeInfo = (dateStr: string | null): string => {
  if (!dateStr) return "No due date";
  const due = new Date(dateStr);
  due.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.ceil(
    (due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24),
  );
  if (days < 0)
    return `${Math.abs(days)} day${Math.abs(days) !== 1 ? "s" : ""} overdue`;
  if (days === 0) return "Due today";
  if (days === 1) return "Due tomorrow";
  return `Due ${formatDate(dateStr)}`;
};

const toAssignmentItem = (a: RawAssignment): JobCardItem | null => {
  const bucket = resolveBucket("assignment", a.status, a.due_date);
  if (!bucket) return null;
  return {
    id: `assignment-${a.id}`,
    rawId: a.id,
    kind: "assignment",
    sourceProposalId: a.source_proposal_id ?? null,
    title: a.title ?? "Untitled assignment",
    internName: a.intern_name,
    internProfilePicture: a.intern_profile_picture ?? null,
    description: a.description ?? "",
    dueDate: a.due_date,
    reward: Number(a.reward ?? 0),
    status: a.status,
    createdAt: a.created_at,
    bucket,
  };
};

const toProposalItem = (p: RawProposal): JobCardItem | null => {
  const bucket = resolveBucket("proposal", p.status, p.deadline);
  if (!bucket) return null;
  return {
    id: `proposal-${p.id}`,
    rawId: p.id,
    kind: "proposal",
    title: p.title ?? "Untitled proposal",
    internName: p.intern_name,
    internProfilePicture: p.intern_profile_picture ?? null,
    description: p.description ?? "",
    dueDate: p.deadline,
    reward: Number(p.estimated_reward ?? 0),
    status: p.status,
    createdAt: p.created_at,
    bucket,
  };
};

// const fetchPaginated = async <T,>(url: string): Promise<T[]> => {
//   let all: T[] = [];
//   let next: string | null = url;
//   while (next) {
//     const res: { data: { results?: T[]; next?: string | null } | T[] } =
//       await apiService.get(next);
//     if (Array.isArray(res.data)) {
//       all = res.data;
//       break;
//     }
//     const paged = res.data as { results?: T[]; next?: string | null };
//     all = [...all, ...(paged.results ?? [])];
//     if (paged.next) {
//       const u = new URL(paged.next);
//       next = u.pathname + u.search;
//     } else {
//       next = null;
//     }
//   }
//   return all;
// };

// Single combined dashboard endpoint returning assignments + proposals.
// Note: backend registers under `/api/job-cards/home/` (plural "job-cards").

type HomeOverviewResponse = {
  assignments?: RawAssignment[];
  proposals?: RawProposal[];
};

const fetchAllJobCardItems = async (): Promise<JobCardItem[]> => {
  const res = await apiService.get("/api/job-card/job-cards/home/");
  const data = (res.data || {}) as HomeOverviewResponse;
  const rawAssignments: RawAssignment[] = data.assignments ?? [];
  const rawProposals: RawProposal[] = data.proposals ?? [];

  const assignmentItems = rawAssignments
    .map(toAssignmentItem)
    .filter((x): x is JobCardItem => !!x);
  const proposalItems = rawProposals
    .map(toProposalItem)
    .filter((x): x is JobCardItem => !!x);
  return [...assignmentItems, ...proposalItems];
};

export const fetchJobCardCounts = async () => {
  const items = await fetchAllJobCardItems();
  return {
    overdue: items.filter((i) => i.bucket === "overdue").length,
    inProgress: items.filter((i) => i.bucket === "in-progress").length,
    needsReview: items.filter((i) => i.bucket === "needs-review").length,
  };
};

// ─── Constants ────────────────────────────────────────────────────────────────

const bounceKeyframe = {
  "@keyframes bounce": {
    "0%, 100%": { transform: "translateY(0)" },
    "50%": { transform: "translateY(-2px)" },
  },
};

const MOBILE_TABS: { key: MobileTab; label: string; color: string }[] = [
  { key: "summary", label: "Summary", color: "#94a3b8" },
  { key: "needs-review", label: "Written Proposals", color: "#3b82f6" },
  { key: "in-progress", label: "Upcoming Job Cards", color: "#f59e0b" },
  { key: "overdue", label: "Overdue", color: "#E11D48" },
];

interface JobCardActivityProps {
  expanded: boolean;
  onMinimise: () => void;
  onNext?: () => void;
  onPrev?: () => void;
  onCountsChange?: (counts: {
    overdue: number;
    inProgress: number;
    needsReview: number;
  }) => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

const JobCardActivity = ({
  expanded,
  onMinimise,
  onPrev,
  onNext,
  onCountsChange,
}: JobCardActivityProps) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const scrollRef = useRef<HTMLDivElement>(null);

  const [mobileTab, setMobileTab] = useState<MobileTab>("summary");
  const [selectedItem, setSelectedItem] = useState<JobCardItem | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [items, setItems] = useState<JobCardItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);

  const loadItems = async () => {
    setLoading(true);
    try {
      const all = await fetchAllJobCardItems();
      setItems(all);
      setLoadError(false);
      onCountsChange?.({
        overdue: all.filter((i) => i.bucket === "overdue").length,
        inProgress: all.filter((i) => i.bucket === "in-progress").length,
        needsReview: all.filter((i) => i.bucket === "needs-review").length,
      });
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onCountsChange]);

  const sorted = [...items].sort((a, b) => {
    if (!a.dueDate) return 1;
    if (!b.dueDate) return -1;
    return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
  });

  const overdueCount = items.filter((i) => i.bucket === "overdue").length;
  const inProgressCount = items.filter(
    (i) => i.bucket === "in-progress",
  ).length;
  const needsReviewCount = items.filter(
    (i) => i.bucket === "needs-review",
  ).length;
  const interns = Array.from(new Set(items.map((i) => i.internName)));

  if (!expanded) {
    return (
      <Box
        sx={{ display: "flex", alignItems: "flex-end", alignSelf: "stretch" }}
      >
        <JobCardActivityMinimisedPill
          overdueCount={overdueCount}
          inProgressCount={inProgressCount}
          needsReviewCount={needsReviewCount}
          onExpand={onMinimise}
        />
      </Box>
    );
  }

  const statusColumns: {
    bucket: JobCardBucket;
    label: string;
    color: string;
  }[] = [
    { bucket: "needs-review", label: "WRITTEN PROPOSALS", color: "#3b82f6" },
    { bucket: "in-progress", label: "UPCOMING JOB CARDS", color: "#f59e0b" },
    { bucket: "overdue", label: "OVER DUE", color: "#E11D48" },
  ];

  const TopNav = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        mb: 2,
      }}
    >
      <IconButton
        onClick={() => onPrev?.()}
        sx={{
          backgroundColor: colors.primary[400],
          color: "white",
          width: 32,
          height: 32,
          "&:hover": { backgroundColor: colors.primary[300] },
        }}
      >
        <ChevronLeft sx={{ fontSize: 18 }} />
      </IconButton>

      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "0.9rem",
          color: colors.primary[100],
          minWidth: { xs: 140, sm: 180 },
          textAlign: "center",
        }}
      >
        Job Card Activity
      </Typography>

      <IconButton
        onClick={() => onNext?.()}
        sx={{
          backgroundColor: colors.primary[400],
          color: "white",
          width: 32,
          height: 32,
          "&:hover": { backgroundColor: colors.primary[300] },
        }}
      >
        <ChevronRight sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );

  const renderItemRow = (item: JobCardItem) => (
    <Box
      key={item.id}
      onClick={() => {
        setSelectedItem(item);
        setModalOpen(true);
      }}
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 1,
        p: 1.5,
        borderRadius: "16px",
        backgroundColor: colors.primary[900],
        cursor: "pointer",
        transition: "background-color 0.15s ease",
        "&:hover": { backgroundColor: colors.primary[700] + "40" },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Avatar
          src={item.internProfilePicture ?? undefined}
          sx={{ width: 36, height: 36, flexShrink: 0 }}
        >
          {item.internName[0]}
        </Avatar>
        <Typography
          sx={{
            flex: 1,
            fontWeight: 600,
            fontSize: "0.8rem",
            color: colors.primary[100],
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {item.title}
        </Typography>
        <ChevronRight
          sx={{ fontSize: 16, color: colors.primary[700], flexShrink: 0 }}
        />
      </Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pl: "48px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <AccessTime sx={{ fontSize: 14, color: colors.primary[700] }} />
          <Typography sx={{ fontSize: "0.7rem", color: colors.primary[700] }}>
            {formatTimeInfo(item.dueDate)}
          </Typography>
        </Box>
        {item.reward > 0 && (
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: colors.primary[100],
            }}
          >
            ${item.reward.toFixed(2)}
          </Typography>
        )}
      </Box>
    </Box>
  );

  // ─── Mobile layout ────────────────────────────────────────────────────────
  if (isMobile) {
    const activeTabConfig = MOBILE_TABS.find((t) => t.key === mobileTab)!;
    const tabCountMap: Record<string, number> = {
      summary: items.length,
      overdue: overdueCount,
      "in-progress": inProgressCount,
      "needs-review": needsReviewCount,
    };
    const mobileItems =
      mobileTab !== "summary"
        ? sorted.filter((i) => i.bucket === (mobileTab as JobCardBucket))
        : [];

    return (
      <Box sx={{ width: "100%", px: 1 }}>
        <Box
          sx={{
            border: `1px solid ${colors.primary[300]}30`,
            borderRadius: "24px",
            p: 2,
            backgroundColor: colors.primary[900],
            position: "relative",
          }}
        >
          <Tooltip title="Minimise" arrow>
            <IconButton
              onClick={onMinimise}
              size="small"
              sx={{
                position: "absolute",
                top: 10,
                right: 12,
                color: colors.primary[700],
                zIndex: 3,
                "&:hover": { color: colors.primary[400] },
              }}
            >
              <KeyboardArrowDown sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>

          {TopNav}

          {loadError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Job Card activity could not be loaded. Please refresh and try again.
            </Alert>
          )}

          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              mb: 2,
              backgroundColor: colors.primary[800],
              borderRadius: "14px",
              p: 0.5,
            }}
          >
            {MOBILE_TABS.map((tab) => {
              const isActive = mobileTab === tab.key;
              const count = tabCountMap[tab.key] ?? 0;
              return (
                <Box
                  key={tab.key}
                  onClick={() => setMobileTab(tab.key)}
                  sx={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 0.3,
                    py: 1,
                    px: 0.5,
                    borderRadius: "10px",
                    cursor: "pointer",
                    backgroundColor: isActive
                      ? colors.primary[900]
                      : "transparent",
                    transition: "background-color 0.2s ease",
                    borderBottom: isActive
                      ? `2px solid ${tab.color}`
                      : "2px solid transparent",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.62rem",
                      fontWeight: isActive ? 700 : 500,
                      color: isActive
                        ? colors.primary[400]
                        : colors.primary[700],
                      lineHeight: 1,
                      textAlign: "center",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {tab.label}
                  </Typography>
                  {tab.key !== "summary" && (
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 700,
                        color: isActive ? tab.color : colors.primary[700],
                        lineHeight: 1,
                      }}
                    >
                      {count}
                    </Typography>
                  )}
                </Box>
              );
            })}
          </Box>

          {mobileTab === "summary" ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box
                sx={{
                  backgroundColor: colors.primary[800],
                  borderRadius: "16px",
                  p: 2,
                  display: "flex",
                  flexDirection: "column",
                  gap: 1.5,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.65rem",
                    letterSpacing: "0.1em",
                    color: colors.primary[700],
                    mb: 0.5,
                  }}
                >
                  STATUS OVERVIEW
                </Typography>
                {[
                  {
                    label: "Written Proposals",
                    count: needsReviewCount,
                    color: "#3b82f6",
                    tab: "needs-review" as MobileTab,
                  },
                  {
                    label: "Upcoming Job Cards",
                    count: inProgressCount,
                    color: "#f59e0b",
                    tab: "in-progress" as MobileTab,
                  },
                  {
                    label: "Over Due",
                    count: overdueCount,
                    color: "#E11D48",
                    tab: "overdue" as MobileTab,
                  },
                ].map(({ label, count, color, tab }) => (
                  <Box
                    key={label}
                    onClick={() => setMobileTab(tab)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      py: 0.5,
                    }}
                  >
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: "50%",
                          backgroundColor: color,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        sx={{ fontSize: "0.85rem", color: colors.primary[100] }}
                      >
                        {label}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Chip
                        label={loadError ? "—" : count}
                        size="small"
                        sx={{
                          height: 22,
                          fontSize: "0.72rem",
                          fontWeight: 700,
                          backgroundColor: color + "20",
                          color,
                          "& .MuiChip-label": { px: 1.2 },
                        }}
                      />
                      <ChevronRight
                        sx={{ fontSize: 14, color: colors.primary[700] }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>

              <Box
                sx={{
                  backgroundColor: colors.primary[800],
                  borderRadius: "16px",
                  p: 2,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.65rem",
                    letterSpacing: "0.1em",
                    color: colors.primary[700],
                    mb: 1.5,
                  }}
                >
                  INTERNS WITH ACTIVITY
                </Typography>
                <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                  {interns.map((name) => {
                    const count = items.filter(
                      (i) => i.internName === name,
                    ).length;
                    return (
                      <Tooltip
                        key={name}
                        title={`${name}: ${count} item${count !== 1 ? "s" : ""}`}
                        arrow
                      >
                        <Box sx={{ position: "relative", cursor: "pointer" }}>
                          <Avatar
                            sx={{
                              width: 44,
                              height: 44,
                              border: `2px solid ${colors.primary[700]}`,
                            }}
                          >
                            {name[0]}
                          </Avatar>
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: -3,
                              right: -3,
                              width: 18,
                              height: 18,
                              borderRadius: "50%",
                              backgroundColor: "#3b82f6",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              border: `1.5px solid ${colors.primary[900]}`,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "0.6rem",
                                color: "white",
                                fontWeight: 700,
                                lineHeight: 1,
                              }}
                            >
                              {count}
                            </Typography>
                          </Box>
                        </Box>
                      </Tooltip>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
                maxHeight: "60vh",
                overflowY: "auto",
                scrollbarWidth: "none",
                "&::-webkit-scrollbar": { display: "none" },
                pb: 1,
              }}
            >
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                  <CircularProgress
                    size={24}
                    sx={{ color: activeTabConfig.color }}
                  />
                </Box>
              ) : loadError ? (
                <Alert severity="error">
                  Job Card activity could not be loaded. Please refresh and try again.
                </Alert>
              ) : mobileItems.length === 0 ? (
                <Box
                  sx={{
                    py: 6,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "50%",
                      backgroundColor: activeTabConfig.color + "20",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: activeTabConfig.color,
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{ fontSize: "0.8rem", color: colors.primary[400] }}
                  >
                    No {activeTabConfig.label.toLowerCase()} items
                  </Typography>
                </Box>
              ) : (
                mobileItems.map((item) => renderItemRow(item))
              )}
            </Box>
          )}
        </Box>

        <JobCardItemDetailsModal
          open={modalOpen}
          item={selectedItem}
          onClose={() => setModalOpen(false)}
          onActionComplete={loadItems}
        />
      </Box>
    );
  }

  // ─── Desktop layout ───────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        position: "relative",
        width: "fit-content",
        maxWidth: { xs: "calc(100vw - 32px)", md: "fit-content" },
      }}
    >
      <Box
        sx={{
          border: `1px solid ${colors.primary[300]}30`,
          borderRadius: "40px",
          p: { xs: 1.5, md: 3 },
          boxShadow: "0 6px 28px rgba(7, 7, 7, 0.06)",
          backgroundColor: colors.primary[900],
          position: "relative",
        }}
      >
        <Tooltip title="Minimise" arrow>
          <IconButton
            onClick={onMinimise}
            size="small"
            sx={{
              position: "absolute",
              top: 12,
              right: 16,
              zIndex: 3,
              color: colors.primary[700],
              "&:hover": { color: colors.primary[400] },
            }}
          >
            <KeyboardArrowDown sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>

        {TopNav}

        {loadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Job Card activity could not be loaded. Please refresh and try again.
          </Alert>
        )}

        <Box
          ref={scrollRef}
          sx={{
            display: "flex",
            gap: 2,
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {/* Summary panel */}
          <Box
            sx={{
              minWidth: { xs: 200, sm: 240, md: 260 },
              maxWidth: { xs: 200, sm: 240, md: 260 },
              backgroundColor: colors.primary[800],
              borderRadius: "30px",
              p: 2.5,
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              gap: 2,
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "0.7rem",
                letterSpacing: "0.1em",
                color: colors.primary[700],
              }}
            >
              JOB CARD ACTIVITY
            </Typography>

            <Box
              sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 2 }}
            >
              {[
                {
                  label: "Upcoming Job Cards",
                  count: inProgressCount,
                  color: "#f0b445",
                },
                {
                  label: "Written Proposals",
                  count: needsReviewCount,
                  color: "#3b82f6",
                },
              ].map(({ label, count, color }) => (
                <Box
                  key={label}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        backgroundColor: color,
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{ fontSize: "0.8rem", color: colors.primary[100] }}
                    >
                      {label}
                    </Typography>
                  </Box>
                  <Chip
                    label={loadError ? "—" : count}
                    size="small"
                    sx={{
                      height: 20,
                      fontSize: "0.7rem",
                      backgroundColor: colors.primary[700] + "30",
                      color: colors.primary[100],
                      "& .MuiChip-label": { px: 1 },
                    }}
                  />
                </Box>
              ))}
            </Box>

            <Box
              sx={{
                borderRadius: "20px",
                backgroundColor: colors.primary[900],
                px: 1.5,
                py: 2,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: colors.primary[700],
                  mb: 1.5,
                }}
              >
                Interns
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                {interns.map((name) => {
                  const count = items.filter(
                    (i) => i.internName === name,
                  ).length;
                  return (
                    <Tooltip
                      key={name}
                      title={`${name}: ${count} item${count !== 1 ? "s" : ""}`}
                      arrow
                    >
                      <Box sx={{ position: "relative", cursor: "pointer" }}>
                        <Avatar
                          sx={{
                            width: 40,
                            height: 40,
                            border: `2px solid ${colors.primary[400]}`,
                          }}
                        >
                          {name[0]}
                        </Avatar>
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: -4,
                            right: -4,
                            width: 18,
                            height: 18,
                            borderRadius: "50%",
                            backgroundColor: "#3b82f6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: `1.5px solid ${colors.primary[900]}`,
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "0.6rem",
                              color: "white",
                              fontWeight: 700,
                              lineHeight: 1,
                            }}
                          >
                            {count}
                          </Typography>
                        </Box>
                      </Box>
                    </Tooltip>
                  );
                })}
              </Box>
            </Box>
          </Box>

          {/* Status columns */}
          {statusColumns.map(({ bucket, label, color }) => {
            const columnItems = sorted.filter((i) => i.bucket === bucket);
            return (
              <Box
                key={bucket}
                sx={{
                  minWidth: { xs: 200, sm: 240, md: 260 },
                  maxWidth: { xs: 200, sm: 240, md: 260 },
                  borderRadius: "30px",
                  p: 2,
                  backgroundColor: colors.primary[800],
                  flexShrink: 0,
                  position: "relative",
                  borderTop: `1.5px solid ${color}${loading ? "10" : "40"}`,
                  borderLeft: `1.5px solid ${color}${loading ? "10" : "40"}`,
                  borderRight: `1.5px solid ${color}${loading ? "10" : "40"}`,
                  borderBottom: loading
                    ? `4px solid transparent`
                    : `4px solid ${color}60`,
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 700,
                    fontSize: "0.75rem",
                    letterSpacing: "0.08em",
                    color,
                    mb: 1.5,
                  }}
                >
                  {label}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    maxHeight: 3 * 72 + 2 * 8,
                    overflowY: "auto",
                    scrollbarWidth: "none",
                    "&::-webkit-scrollbar": { display: "none" },
                  }}
                >
                  {loading ? (
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        py: 4,
                      }}
                    >
                      <CircularProgress size={20} sx={{ color }} />
                    </Box>
                  ) : loadError ? (
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: colors.primary[700],
                        textAlign: "center",
                        mt: 2,
                      }}
                    >
                      Unable to load
                    </Typography>
                  ) : columnItems.length === 0 ? (
                    <Typography
                      sx={{
                        fontSize: "0.75rem",
                        color: colors.primary[700],
                        textAlign: "center",
                        mt: 2,
                      }}
                    >
                      No entries
                    </Typography>
                  ) : (
                    columnItems.map((item) => renderItemRow(item))
                  )}
                </Box>

                {!loading && columnItems.length > 3 && (
                  <Box
                    sx={{
                      ...bounceKeyframe,
                      position: "absolute",
                      bottom: 4,
                      left: "50%",
                      transform: "translateX(-50%)",
                      zIndex: 2,
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      animation: "bounce 1.2s ease-in-out infinite",
                    }}
                  >
                    <KeyboardArrowDown
                      sx={{
                        fontSize: 18,
                        color: colors.primary[400],
                        mt: "-6px",
                      }}
                    />
                    <KeyboardArrowDown
                      sx={{
                        fontSize: 18,
                        color: colors.primary[400],
                        mt: "-10px",
                        opacity: 0.5,
                      }}
                    />
                  </Box>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>

      <JobCardItemDetailsModal
        open={modalOpen}
        item={selectedItem}
        onClose={() => setModalOpen(false)}
        onActionComplete={loadItems}
      />
    </Box>
  );
};

export default JobCardActivity;
